// components/user-tour/DemoTour.jsx
"use client";

import { Joyride, STATUS } from "react-joyride";
import { waitForTourTarget } from "../../hooks/useDemoTour";
import { useRef, useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { TEMPLATES_FEATURE_ENABLED } from "../../context/FeatureFlagsContext";

const waitForStepTarget = (selector, timeoutMs = 2500) => async () => {
  await waitForTourTarget(selector, timeoutMs);
};

// ---------------------------------------------------------------------------
// Backdrop (gray dim) settings
// ---------------------------------------------------------------------------
// Color of the gray layer that covers everything except the active target.
const BACKDROP_COLOR = "rgba(31, 41, 55, 0.55)"; // gray-800 @ 55%
const PILL_STEP_IDS = new Set(["capture", "patient", "recording"]);

function getStepSpotlightMetrics(step) {
  if (step?.id === "capture") return { padding: 8, radius: 999 };
  if (step?.id === "patient" || step?.id === "new-visit") return { padding: 6, radius: 16 };
  return { padding: 4, radius: 8 };
}
// Must stay BELOW Joyride's zIndex (10000) so the tooltip sits above the gray.
const BACKDROP_Z_INDEX = 9999;

const OVERFLOW_CLIP_RE = /(auto|scroll|hidden|clip|overlay)/;

/**
 * Returns every ancestor of `el` that clips its children
 * (overflow auto/scroll/hidden/clip). These are the containers
 * that can hide part of the target when the user scrolls.
 */
function getClippingAncestors(el) {
  const list = [];
  let node = el.parentElement;
  while (
    node &&
    node !== document.body &&
    node !== document.documentElement
  ) {
    const cs = window.getComputedStyle(node);
    const clipsX = OVERFLOW_CLIP_RE.test(cs.overflowX);
    const clipsY = OVERFLOW_CLIP_RE.test(cs.overflowY);
    if (clipsX || clipsY) list.push({ node, clipsX, clipsY });
    node = node.parentElement;
  }
  return list;
}

/**
 * Computes the part of the target that is actually VISIBLE on screen:
 * target rect (+ padding) intersected with every clipping ancestor
 * and the viewport.
 *
 * Returns:
 *   - { top, left, width, height } when some part is visible
 *   - { hidden: true } when the target is completely scrolled out of view
 */
function getVisibleRect(el, ancestors, padding = 0) {
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return { hidden: true };

  let top = r.top - padding;
  let left = r.left - padding;
  let right = r.right + padding;
  let bottom = r.bottom + padding;

  for (const { node, clipsX, clipsY } of ancestors) {
    const pr = node.getBoundingClientRect();
    if (clipsY) {
      top = Math.max(top, pr.top);
      bottom = Math.min(bottom, pr.bottom);
    }
    if (clipsX) {
      left = Math.max(left, pr.left);
      right = Math.min(right, pr.right);
    }
  }

  // Clamp to the viewport.
  top = Math.max(top, 0);
  left = Math.max(left, 0);
  bottom = Math.min(bottom, window.innerHeight);
  right = Math.min(right, window.innerWidth);

  if (right <= left || bottom <= top) return { hidden: true };

  return { top, left, width: right - left, height: bottom - top };
}

/**
 * Tracks the VISIBLE on-screen area of the element matching `selector`.
 * Uses requestAnimationFrame so it follows scrolling, resizing and
 * layout animations, but only triggers a re-render when the rect changes.
 */
function useTargetRect(selector, active, padding = 0) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!active || !selector || typeof window === "undefined") {
      setRect(null);
      return;
    }

    let frameId;
    let lastKey = "";
    let lastEl = null;
    let ancestors = [];

    const tick = () => {
      let next = null;
      try {
        const el = document.querySelector(selector);
        if (el) {
          // Re-scan clipping parents only when the target element changes.
          if (el !== lastEl) {
            lastEl = el;
            ancestors = getClippingAncestors(el);
          }
          next = getVisibleRect(el, ancestors, padding);
        } else {
          lastEl = null;
          ancestors = [];
        }
      } catch {
        next = null;
      }

      const key = !next
        ? "none"
        : next.hidden
        ? "hidden"
        : `${Math.round(next.top)}|${Math.round(next.left)}|${Math.round(
            next.width
          )}|${Math.round(next.height)}`;

      if (key !== lastKey) {
        lastKey = key;
        setRect(next);
      }

      frameId = requestAnimationFrame(tick);
    };

    tick();
    return () => cancelAnimationFrame(frameId);
  }, [selector, active, padding]);

  return rect;
}

/**
 * Visual-only gray backdrop with a bright cutout around the VISIBLE part
 * of the active target.
 * pointer-events: none -> it never blocks or intercepts clicks, so the
 * existing Joyride click behavior (overlay blocking + spotlightClicks)
 * is completely unchanged.
 */
function TourSpotlightRing({ rect, radius }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted || !rect || rect.hidden) return null;

  return createPortal(
    <div
      aria-hidden="true"
      data-tour-spotlight-ring
      style={{
        position: "fixed",
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        borderRadius: radius,
        zIndex: BACKDROP_Z_INDEX + 2,
        pointerEvents: "none",
      }}
    />,
    document.body
  );
}

function TourBackdrop({ rect, radius = 8 }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  if (!mounted || !rect) return null;

  const baseStyle = {
    position: "fixed",
    pointerEvents: "none",
    zIndex: BACKDROP_Z_INDEX,
  };

  // Target fully scrolled out of view -> keep the whole screen gray.
  if (rect.hidden) {
    return createPortal(
      <div
        aria-hidden="true"
        data-tour-backdrop
        style={{ ...baseStyle, inset: 0, backgroundColor: BACKDROP_COLOR }}
      />,
      document.body
    );
  }

  // No position transition: the cutout must stick to the target while scrolling.
  const style = {
    ...baseStyle,
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    borderRadius: radius,
    // Huge spread shadow paints gray over everything outside the cutout.
    boxShadow: `0 0 0 200vmax ${BACKDROP_COLOR}`,
  };

  return createPortal(
    <div aria-hidden="true" data-tour-backdrop style={style} />,
    document.body
  );
}

const ALL_DEMO_TOUR_STEPS = [
  // Create a new visit
  {
    id: "new-visit",
    target: '[data-tour="new-visit"]',
    mobileContent: (
      <p className="text-sm leading-snug text-gray-700">
        Tap <strong>New visit</strong> to start.
      </p>
    ),
    content: (
      <div>
        <h3 className="text-lg font-bold text-gray-800">🚀 Start a New Visit</h3>
        <p className="text-sm text-gray-600 mt-1">
          Click <strong>New visit</strong> to create a fresh patient session.
          This is where every documentation workflow begins.
        </p>
        <div className="mt-2 p-2 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-700">
            💡 The new visit appears in your sidebar list.
          </p>
        </div>
      </div>
    ),
    placement: "right",
    offset: 15,
    skipBeacon: true,
    spotlightClicks: true,
    before: waitForStepTarget('[data-tour="new-visit"]'),
  },

  // Fill patient details
  {
    id: "patient",
    target: '[data-tour="patient-tab"]',
    mobileContent: (
      <p className="text-sm leading-snug text-gray-700">
        Fill in the <strong>patient details</strong> here.
      </p>
    ),
    content: (
      <div>
        <h3 className="text-lg font-bold text-gray-800">📋 Patient Details</h3>
        <p className="text-sm text-gray-600 mt-1">
          Enter the patient's name, age, gender, and visit type.
          This information is used to personalize the SOAP note.
        </p>
        <div className="mt-2 p-2 bg-green-50 rounded-lg">
          <p className="text-xs text-green-700">
            Required before you can start recording or upload audio.
          </p>
        </div>
      </div>
    ),
    placement: "left-start",
    offset: 8,
    skipBeacon: true,
    spotlightClicks: true,
    before: waitForStepTarget('[data-tour="patient-tab"]'),
    styles: {
      tooltip: {
        minWidth: "260px",
        maxWidth: "300px",
        padding: "14px",
      },
    },
  },

  // Choose a template — omitted while Templates are feature-flagged off
  {
    id: "templates",
    target: '[data-tour="templates-menu"]',
    mobileContent: (
      <p className="text-sm leading-snug text-gray-700">
        Pick a <strong>template</strong> for this visit.
      </p>
    ),
    content: (
      <div>
        <h3 className="text-lg font-bold text-gray-800">📁 Choose a Template</h3>
        <p className="text-sm text-gray-600 mt-1">
          Select a template that best fits your visit type.
        </p>
        <div className="mt-2 p-2 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-700">
            💡 You can switch templates anytime – the note regenerates automatically.
          </p>
        </div>
      </div>
    ),
    placement: "left",
    offset: 15,
    skipBeacon: true,
    spotlightClicks: true,
    before: waitForStepTarget('[data-tour="templates-menu"]'),
  },

  // Recording source
  {
    id: "recording",
    target: '[data-tour="recording-options-menu"]',
    mobileContent: (
      <p className="text-sm leading-snug text-gray-700">
        Choose your <strong>audio source</strong>.
      </p>
    ),
    content: (
      <div>
        <h3 className="text-lg font-bold text-gray-800">🎤 Recording Source</h3>
        <p className="text-sm text-gray-600 mt-1">
          Select your audio source: <strong>Microphone only</strong>,{" "}
          <strong>Computer audio only</strong>, or <strong>Both</strong>.
        </p>
        <div className="mt-2 p-2 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-700">
            💡 Choose "Both" to capture your mic and the computer audio (e.g., telehealth calls).
          </p>
        </div>
      </div>
    ),
    placement: "left",
    offset: 8,
    skipBeacon: true,
    spotlightClicks: true,
    before: waitForStepTarget('[data-tour="recording-options-menu"]'),
  },

  // Start capturing
  {
    id: "capture",
    target: '[data-tour="recording-controls"]',
    mobileContent: (
      <p className="text-sm leading-snug text-gray-700">
        Tap <strong>Capture</strong> to record, or <strong>Next</strong> for a quick demo.
      </p>
    ),
    content: (
      <div>
        <h3 className="text-lg font-bold text-gray-800">🎙️ Start Capturing</h3>
        <p className="text-sm text-gray-600 mt-1">
          Click <strong>Capture</strong> to begin transcribing the live conversation.
          The AI will listen and produce a real-time transcript.
        </p>
        <div className="mt-2 p-2 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-700">
            ⏸️ You can pause/resume anytime. The SOAP note gets generated after you stop the recording.
          </p>
        </div>
        <div className="mt-2 p-2 bg-green-50 rounded-lg">
          <p className="text-xs text-green-700">
            ⚡ Or press <strong>Next</strong> to run a quick demo capture and jump straight to the SOAP note.
          </p>
        </div>
      </div>
    ),
    placement: "left",
    offset: 8,
    skipBeacon: true,
    spotlightClicks: true,
    before: waitForStepTarget('[data-tour="recording-controls"]'),
  },

  // Review the SOAP note
  {
    id: "soap",
    target: '[data-tour="soap-section"]',
    mobileContent: (
      <p className="text-sm leading-snug text-gray-700">
        Your <strong>SOAP note</strong> appears here.
      </p>
    ),
    content: (
      <div>
        <h3 className="text-lg font-bold text-gray-800">📄 SOAP Note</h3>
        <p className="text-sm text-gray-600 mt-1">
          Generated after visit ends. Click any section to edit it inline.
        </p>
        <div className="mt-2 p-2 bg-green-50 rounded-lg">
          <p className="text-xs text-green-700">All sections editable inline.</p>
        </div>
      </div>
    ),
    placement: "left",
    offset: 10,
    skipBeacon: true,
    spotlightClicks: true,
    before: waitForStepTarget('[data-tour="soap-section"]'),
  },
];

export const DEMO_TOUR_STEPS = ALL_DEMO_TOUR_STEPS.filter(
  (step) => TEMPLATES_FEATURE_ENABLED || step.id !== "templates",
);

export const getTourStepIndex = (id) =>
  DEMO_TOUR_STEPS.findIndex((step) => step.id === id);

export const TOUR_STEP = {
  NEW_VISIT: getTourStepIndex("new-visit"),
  PATIENT: getTourStepIndex("patient"),
  TEMPLATES: getTourStepIndex("templates"),
  RECORDING: getTourStepIndex("recording"),
  CAPTURE: getTourStepIndex("capture"),
  SOAP: getTourStepIndex("soap"),
};

const JOYRIDE_OPTIONS = {
  skipBeacon: true,
  overlayClickAction: false,
  skipScroll: false,
  targetWaitTimeout: 2500,
  zIndex: 10000,
  primaryColor: "#2563eb",
  textColor: "#1f2937",
  backgroundColor: "#ffffff",
  arrowColor: "#ffffff",
  buttons: ["back", "primary"],
  spotlightPadding: 0,
  disableOverlay: false,
};

const JOYRIDE_STYLES = {
  tooltip: {
    borderRadius: "16px",
  },
  // Transparent overlay but still active so clicks outside the target are blocked.
  // The gray dim is drawn separately by <TourBackdrop /> (visual only).
  // "not-allowed" cursor makes it obvious the dimmed area can't be clicked.
  overlay: {
    backgroundColor: "transparent",
    cursor: "not-allowed",
  },
  // Hide the spotlight cutout visual; overlay still blocks background clicks.
  spotlight: {
    display: "none",
    border: "none",
    boxShadow: "none",
    backgroundColor: "transparent",
  },
};

export default function DemoTour({
  run,
  onCallback,
  stepIndex,
  showSkip = false,
  demoCaptureStage = "",
  // True while a demo capture is running (auto via Next, or manual via
  // Capture) or the demo SOAP note is being generated. Used to disable Next
  // on Capture so it can't be re-triggered.
  isDemoCapturing = false,
  // When provided, Next on the Capture step calls this INSTEAD of advancing Joyride.
  // Joyride (controlled mode) would otherwise set lifecycle=COMPLETE, unmount
  // the tooltip and end the tour while we hold stepIndex on Capture. The parent
  // advances to SOAP via stepIndex once the demo SOAP note exists.
  onDemoCaptureNext,
}) {
  const controlsRef = useRef(null);

  // Track viewport so we can shrink the tooltip on phones.
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Flag the document while the tour runs so floating launchers (Magic Editor,
  // Notepad, chat-bot) can step aside on phones instead of sitting on top of the
  // tooltip. See `.tour-hide-on-mobile` in index.css.
  useEffect(() => {
    if (!run) return undefined;
    document.body.setAttribute("data-product-tour", "active");
    return () => document.body.removeAttribute("data-product-tour");
  }, [run]);

  // On mobile:
  //   - Swap each step's rich content for a short one-line message.
  //   - Force placement to "top" so the tooltip always sits above the target.
  const steps = useMemo(() => {
    if (!isMobileView) return DEMO_TOUR_STEPS;
    return DEMO_TOUR_STEPS.map((s) => ({
      ...s,
      content: s.mobileContent ?? s.content,
      placement: "top",
      offset: 10,
      // Strip any desktop-only per-step tooltip widths so mobile sizing wins.
      styles: undefined,
    }));
  }, [isMobileView]);

  const Tooltip = ({
    continuous,
    index,
    step,
    primaryProps,
    backProps,
    tooltipProps,
  }) => {
    const totalSteps = steps.length;
    const isLastStep = index === totalSteps - 1;

    // Capture step: Next is always visible.
    //   - AUTO flow: clicking Next runs a short demo capture, then the SOAP
    //     note is generated and the tour advances to SOAP by itself.
    //   - MANUAL flow: the user clicks Capture, then Stop.
    // While either capture is running, Next is disabled.
    const isCaptureStep = steps[index]?.id === "capture";
    const isCapturing = isCaptureStep && isDemoCapturing;
    const interceptNext =
      isCaptureStep && typeof onDemoCaptureNext === "function";
    const stepStyles = step.styles?.tooltip || {};

    // On Capture in the demo, Next must NOT reach Joyride (see prop docs).
    const nextButtonProps = interceptNext
      ? {
          ...primaryProps,
          onClick: (e) => {
            e?.preventDefault?.();
            e?.stopPropagation?.();
            if (!isCapturing) onDemoCaptureNext();
          },
        }
      : primaryProps;

    const handleSkip = () => {
      if (controlsRef.current?.skip) controlsRef.current.skip();
    };

    // Compact sizing + layout used only on phones.
    const mobileCardStyle = isMobileView
      ? {
          minWidth: "auto",
          maxWidth: "min(calc(100vw - 32px), 240px)",
          width: "min(calc(100vw - 32px), 240px)",
          padding: "10px 12px",
          fontSize: "13px",
        }
      : {};

    return (
      <div
        {...tooltipProps}
        className="rounded-2xl shadow-2xl relative"
        style={{
          minWidth: isMobileView ? "auto" : "260px",
          maxWidth: isMobileView
            ? "min(calc(100vw - 32px), 240px)"
            : "320px",
          padding: isMobileView ? "10px 12px" : "14px",
          ...stepStyles,
          ...mobileCardStyle,
          ...tooltipProps?.style,
          border: "2px solid transparent",
          backgroundImage:
            "linear-gradient(white, white), linear-gradient(to right, #2563eb, #0d9488)",
          backgroundOrigin: "border-box",
          backgroundClip: "padding-box, border-box",
        }}
      >
        {/* Header: step chip. On mobile it stays, but is tighter. */}
        <div
          className={
            isMobileView
              ? "flex items-center justify-between mb-2"
              : "flex items-center justify-between mb-3"
          }
        >
          <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
            Step {index + 1} of {totalSteps}
          </span>
        </div>

        {/* Body: on mobile this is a one-line message, desktop keeps full content. */}
        <div className={isMobileView ? "mb-2" : "mb-4"}>
          {step.content}
        </div>

        {/* Progress bar: hide on mobile to keep the card tiny. */}
        {!isMobileView && (
          <div className="h-1 bg-gray-100 rounded-full mb-3 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-600 to-teal-600 transition-all duration-500"
              style={{ width: `${((index + 1) / totalSteps) * 100}%` }}
            />
          </div>
        )}

        {/* Footer: Skip / Back / Next */}
        <div className="flex justify-between items-center">
          {/* Left side: Skip (restarted tours only; hidden on the last step) */}
          <div>
            {showSkip && !isLastStep && (
              <button
                onClick={handleSkip}
                className={
                  isMobileView
                    ? "px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all text-xs"
                    : "px-3.5 py-1.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all text-sm"
                }
              >
                Skip
              </button>
            )}
          </div>

          {/* Right side: Back (if applicable) and Next/Done */}
          <div className="flex gap-1.5">
            {index > 0 && (
              <button
                {...backProps}
                className={
                  isMobileView
                    ? "px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all text-xs"
                    : "px-3.5 py-1.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all text-sm"
                }
              >
                Back
              </button>
            )}

            {/* Next / Done. On Capture it is disabled while a demo capture is running. */}
            <button
              {...nextButtonProps}
              disabled={isCapturing}
              aria-busy={isCapturing || undefined}
              className={
                (isMobileView
                  ? "px-2.5 py-1 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-lg font-medium hover:shadow-lg transition-all flex items-center gap-1 text-xs"
                  : "px-3.5 py-1.5 bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-1 text-sm") +
                (isCapturing ? " opacity-60 cursor-not-allowed hover:shadow-none" : "")
              }
            >
              {isCapturing ? (
                demoCaptureStage === "generating" ? "Generating…" : "Capturing…"
              ) : continuous ? (
                <>
                  {isLastStep ? "Done" : "Next"}
                  {!isLastStep && (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  )}
                </>
              ) : (
                "Done"
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const joyrideProps = {
    steps,
    run: Boolean(run),
    onEvent: (data, controls) => {
      controlsRef.current = controls;
      if (typeof onCallback === "function") onCallback(data, controls);
    },
    continuous: true,
    scrollToFirstStep: true,
    options: JOYRIDE_OPTIONS,
    styles: JOYRIDE_STYLES,
    tooltipComponent: Tooltip,
  };

  if (typeof stepIndex === "number") {
    joyrideProps.stepIndex = stepIndex;
  }

  const activeStep =
    typeof stepIndex === "number" ? steps[stepIndex] : null;
  const spotlight = getStepSpotlightMetrics(activeStep);

  // Follow the VISIBLE part of the active target so the gray backdrop
  // never lets the highlight spill outside its scroll container.
  const targetRect = useTargetRect(
    activeStep?.target,
    Boolean(run) && Boolean(activeStep),
    spotlight.padding
  );

  return (
    <>
      {activeStep && (
        <style>{`
          ${activeStep.target} {
            position: relative !important;
            outline: none !important;
            ${PILL_STEP_IDS.has(activeStep.id) ? "" : "border-radius: 8px !important;"}
          }
        `}</style>
      )}

      {run && activeStep && (
        <TourBackdrop rect={targetRect} radius={spotlight.radius} />
      )}
      {run && activeStep && (
        <TourSpotlightRing rect={targetRect} radius={spotlight.radius} />
      )}

      <Joyride 
      key={`joyride-${isDemoCapturing ? "busy" : "idle"}`}
      {...joyrideProps} />
    </>
  );
}

export { STATUS };